type AudioPlayOptions = {
  volume?: number; // 0..1
};

let ctx: AudioContext | null = null;
let resumePromise: Promise<void> | null = null;
const bufferCache = new Map<string, Promise<AudioBuffer>>();

let currentSource: AudioBufferSourceNode | null = null;
let currentGain: GainNode | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioCtx =
    window.AudioContext ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).webkitAudioContext;
  if (!AudioCtx) return null;
  if (!ctx) ctx = new AudioCtx();
  return ctx;
}

export function unlockAudio(): void {
  const ac = getAudioContext();
  if (!ac) return;
  if (ac.state === 'running') return;

  // IMPORTANT: call this from a user gesture (e.g. click) for iOS/Safari.
  // Do not `await` here; just trigger resume while we’re still in the gesture handler.
  resumePromise = ac.resume().catch(() => undefined);
}

async function loadBuffer(url: string): Promise<AudioBuffer | null> {
  const ac = getAudioContext();
  if (!ac) return null;

  const cached = bufferCache.get(url);
  if (cached) {
    try {
      return await cached;
    } catch {
      bufferCache.delete(url);
    }
  }

  const p = (async () => {
    try {
      if (resumePromise) await resumePromise;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`fetch ${url} failed: ${res.status}`);
      const ab = await res.arrayBuffer();
      return await ac.decodeAudioData(ab);
    } catch (error) {
      bufferCache.delete(url);
      throw error;
    }
  })();

  bufferCache.set(url, p);
  try {
    return await p;
  } catch {
    return null;
  }
}

export function stopAudio(): void {
  if (currentSource) {
    try {
      currentSource.stop();
    } catch {
      // ignore
    }
    try {
      currentSource.disconnect();
    } catch {
      // ignore
    }
    currentSource = null;
  }
  if (currentGain) {
    try {
      currentGain.disconnect();
    } catch {
      // ignore
    }
    currentGain = null;
  }
}

export async function playAudio(url: string, opts?: AudioPlayOptions): Promise<void> {
  const ac = getAudioContext();
  if (!ac) return;

  const buf = await loadBuffer(url);
  if (!buf) return;

  stopAudio();

  const source = ac.createBufferSource();
  source.buffer = buf;

  const gain = ac.createGain();
  const volume = typeof opts?.volume === 'number' ? opts.volume : 1;
  gain.gain.value = Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 1;

  source.connect(gain);
  gain.connect(ac.destination);

  currentSource = source;
  currentGain = gain;

  source.onended = () => {
    if (currentSource === source) currentSource = null;
    if (currentGain === gain) currentGain = null;
  };

  try {
    source.start(0);
  } catch {
    stopAudio();
  }
}

